import { Flex } from '@mantine/core';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPredicate, Segment } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import { Heading, Modal, Pill } from '@feathr/components';
import { getIconForAction, logUserEvents } from '@feathr/hooks';

import type { ISegmentAIResponse } from './SegmentBuilderAI.controller';
import { generateSegmentFromPrompt } from './SegmentBuilderAI.controller';
import { PROCESSING_STATEMENTS } from './SegmentBuilderAI.utils';
import SegmentBuilderModalActions from './SegmentBuilderModalActions';
import SegmentBuilderModalContent from './SegmentBuilderModalContent';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSegmentGenerated: (predicates: IPredicate[]) => void;
  segment: Segment;
}

type TModalStep = 'input' | 'processing' | 'results'; // eslint-disable-line

function SegmentBuilderAI({
  isOpen,
  onClose,
  onSegmentGenerated,
  segment,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<TModalStep>('input');
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState<ISegmentAIResponse | null>(null);
  const [currentStatementIndex, setCurrentStatementIndex] = useState(0);
  const [selectedExample, setSelectedExample] = useState<string | undefined>(undefined);

  // Check if this is a model failure (has summary) vs a true error
  const isModelFailure =
    !response?.success &&
    response?.summary &&
    response.summary.length > 0 &&
    !(response.summary.length === 1 && response.summary[0].content === 'Error processing response');

  // Cycle through processing statements when in processing mode
  useEffect(() => {
    if (currentStep === 'processing') {
      const interval = setInterval(() => {
        setCurrentStatementIndex((prev) => (prev + 1) % PROCESSING_STATEMENTS.length);
      }, 5000);

      return (): void => clearInterval(interval);
    }

    return undefined;
  }, [currentStep]);

  function handleClose(): void {
    handleStartOver();
    onClose();
  }

  function handleStartOver(): void {
    setCurrentStep('input');
    setPrompt('');
    setResponse(null);
    setCurrentStatementIndex(0);
    setSelectedExample(undefined);
  }

  const handleGenerate = async (): Promise<void> => {
    if (!prompt.trim()) {
      return;
    }

    logUserEvents({ used_pixl_group_builder: { action: 'generate' } });
    setCurrentStep('processing');

    try {
      const result = await generateSegmentFromPrompt(prompt.trim());
      setResponse(result);
      setCurrentStep('results');
    } catch (error) {
      setResponse({
        summary: [
          {
            type: 'text',
            content: 'An unexpected error occurred',
          },
        ],
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      setCurrentStep('results');
    }
  };

  const handleApplySegment = (): void => {
    if (!response?.success || !response.segmentJson) {
      return;
    }

    try {
      const { segmentJson } = response;

      runInAction(() => {
        // Prepare the complete segment update
        const updateData = { ...segmentJson };

        // AI-generated predicates arrive without row keys; mint them (recursing
        // into group rules) so the editor can address the rows it renders.
        if (updateData.predicates) {
          updateData.predicates = updateData.predicates.map((predicate) =>
            ensurePredicateKeys(predicate),
          );
        }

        // Use the segment's built-in set method which handles validation and triggers reactivity
        segment.set(updateData);

        // For predicates, also notify the EditFilters component directly for immediate UI update
        if (updateData.predicates) {
          onSegmentGenerated(updateData.predicates);
        }
      });

      logUserEvents({ used_pixl_group_builder: { action: 'apply' } });
      handleClose();
    } catch (error) {
      setResponse({
        summary: response.summary,
        success: false,
        error: 'Failed to apply Group configuration',
      });
    }
  };

  const getModalContent = (): JSX.Element => {
    return (
      <SegmentBuilderModalContent
        currentStatementIndex={currentStatementIndex}
        currentStep={currentStep}
        prompt={prompt}
        response={response}
        selectedExample={selectedExample}
        setPrompt={setPrompt}
        setSelectedExample={setSelectedExample}
      />
    );
  };

  const getModalFooter = (): JSX.Element | undefined => {
    // Don't show an empty footer in the processing step.
    if (currentStep === 'processing') {
      return undefined;
    }

    return (
      <SegmentBuilderModalActions
        currentStep={currentStep}
        isModelFailure={isModelFailure}
        isResponseFailure={response?.success === false}
        onApply={handleApplySegment}
        onCancel={handleClose}
        onGenerate={handleGenerate}
        onStartOver={handleStartOver}
        prompt={prompt}
      />
    );
  };

  const getModalTitle = (): JSX.Element => {
    return (
      <Flex gap={8}>
        <Heading order={2} variant={'h5'}>
          {t('Pixl Group Builder')}
        </Heading>
        <Pill prefix={getIconForAction('magic')} theme={'purple'}>
          {t('Beta')}
        </Pill>
      </Flex>
    );
  };

  return (
    <Modal
      closeOnClickOutside={currentStep !== 'processing'}
      closeOnEscape={currentStep !== 'processing'}
      description={t('Create smarter Groups using Feathr Intelligence')}
      onClose={handleClose}
      opened={isOpen}
      rightActions={getModalFooter()}
      title={getModalTitle()}
    >
      {getModalContent()}
    </Modal>
  );
}

export default observer(SegmentBuilderAI);
